
import { PlusCircle } from "react-feather";
import { Link } from "react-router-dom";
import { Select, DatePicker, Modal, Form, Input, Button } from "antd";
import { Table, Popconfirm } from "antd";
import React, { useState, useEffect } from "react";
import {
  getAllJobPostings,
  getJobPostingById,
  createJobPosting,
  updateJobPosting,
  deleteJobPosting,
  fetchSearchJobPostings,
} from "../../service/recruitmentService";
import { getAllDepartments } from "../../service/ApiServices";
import { ArrowLeft } from "lucide-react";
import moment from "moment";

const JobPostings = () => {
  const [form] = Form.useForm();
  const [openCreateModal, setOpenCreateModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedJobPosting, setSelectedJobPosting] = useState(null);
  const [jobPostings, setJobPostings] = useState([]);
  const [title, setTitle] = useState("");
  const [department, setDepartment] = useState("");
  const [postedDate, setPostedDate] = useState(null);
  const [allDepartments, setAllDepartments] = useState([]);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 5, total: 0 });
  const [pageSizeModalVisible, setPageSizeModalVisible] = useState(false);
  const [tempPageSize, setTempPageSize] = useState(pagination.pageSize);
  const pageSizeOptions = [5, 10, 20, 50, 100];

  useEffect(() => {
    const fetchJobPostings = async () => {
      setLoading(true);
      const data = await getAllJobPostings();
      setLoading(false);
      if (Array.isArray(data)) {
        setJobPostings(data);
        setPagination((prev) => ({ ...prev, total: data.length }));
      } else {
        setJobPostings([]);
        setPagination((prev) => ({ ...prev, total: 0 }));
      }
    };
    fetchJobPostings();
  }, []);

  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        await getAllDepartments({
          search: "",
          setData: setAllDepartments,
          setLoading,
        });
      } catch (error) {
        console.error("Error fetching departments:", error);
      }
    };
    fetchDepartments();
  }, []);

  useEffect(() => {
    if (selectedJobPosting) {
      const fetchJobPosting = async () => {
        const data = await getJobPostingById(selectedJobPosting);
        if (data) {
          form.setFieldsValue({
            title: data.title,
            department: data.department?._id,
            description: data.description,
            requirements: data.requirements?.join(", "),
            qualifications: data.qualifications || [],
            dueDate: data.dueDate ? moment(data.dueDate) : null,
            status: data.status,
          });
        }
      };
      fetchJobPosting();
    } else {
      form.resetFields();
    }
  }, [selectedJobPosting, form]);

  const fetchJobPostings = async () => {
    setLoading(true);
    const data = await getAllJobPostings();
    setLoading(false);
    if (Array.isArray(data)) {
      setJobPostings(data);
      setPagination((prev) => ({ ...prev, total: data.length }));
    } else {
      setJobPostings([]);
      setPagination((prev) => ({ ...prev, total: 0 }));
    }
  };

  const handleChangeDepartment = (value) => {
    setDepartment(value);
  };

  const getSearchJobPostings = async () => {
    const records = await fetchSearchJobPostings({
      setLoading,
      title,
      department,
      postedDate: postedDate ? postedDate.format("YYYY-MM-DD") : "",
    });
    if (Array.isArray(records)) {
      setJobPostings(records);
      setPagination((prev) => ({ ...prev, total: records.length, current: 1 }));
    } else {
      setJobPostings([]);
      setPagination((prev) => ({ ...prev, total: 0, current: 1 }));
    }
  };

  const handleTableChange = (pagination) => {
    setPagination(pagination);
  };

  const handlePageSizeChange = () => {
    const newPagination = { ...pagination, pageSize: tempPageSize, current: 1 };
    setPagination(newPagination);
    setPageSizeModalVisible(false);
  };

  const onFinish = async (values) => {
    setLoading(true);
    try {
      const jobData = {
        ...values,
        requirements: values.requirements
          ? values.requirements.split(",").map((req) => req.trim()).filter((req) => req)
          : [],
        qualifications: values.qualifications || [],
        dueDate: values.dueDate ? values.dueDate.format("YYYY-MM-DD") : null,
      };
      console.log("Sending job data:", jobData);
      if (selectedJobPosting) {
        await updateJobPosting(selectedJobPosting, jobData);
      } else {
        await createJobPosting(jobData);
      }
      await fetchJobPostings();
      setSelectedJobPosting(null);
      setOpenCreateModal(false);
      form.resetFields();
    } catch (error) {
      console.error("Error saving job posting:", error);
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
    },
    {
      title: "Department",
      dataIndex: "department",
      key: "department",
      render: (department) => department?.departmentName || "N/A",
    },
    {
      title: "Posted By",
      dataIndex: "createdBy",
      key: "createdBy",
      render: (createdBy) => createdBy?.full_name || "N/A",
    },
    {
      title: "Posted Date",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (createdAt) => (createdAt ? moment(createdAt).format("YYYY-MM-DD") : "N/A"),
    },
    {
      title: "Due Date",
      dataIndex: "dueDate",
      key: "dueDate",
      render: (dueDate) => (dueDate ? moment(dueDate).format("YYYY-MM-DD") : "N/A"),
    },
    {
      title: "Requirements",
      dataIndex: "requirements",
      key: "requirements",
      render: (requirements) => (requirements?.length ? requirements.join(", ") : "N/A"),
    },
    {
      title: "Qualifications",
      dataIndex: "qualifications",
      key: "qualifications",
      render: (qualifications) =>
        qualifications?.length
          ? qualifications.map((q) => `${q.name}: ${q.expectedLevel}`).join(", ")
          : "N/A",
    },
{
  title: "Status",
  dataIndex: "status",
  key: "status",
  render: (status) => (
    <span
      style={{
        color:
          status === "Open" ? "#008000" : // Green for Open
          status === "Closed" ? "#FF0000" : // Red for Closed
          status === "Draft" ? "#FFA500" : // Orange for Draft
          "inherit",
      }}
    >
      {status || "N/A"}
    </span>
  ),
},
    {
      title: "Action",
      key: "action",
      render: (record) => (
        <td className="action-table-data">
          <Link
            className="me-2 p-2"
            to="#"
            onClick={() => {
              setSelectedJobPosting(record._id);
              setOpenCreateModal(true);
            }}
            title="Edit job posting"
          >
            <i data-feather="edit" className="feather-edit"></i>
          </Link>
          <Popconfirm
            title="Delete the Job Posting"
            description="Are you sure to delete this job posting?"
            onConfirm={async () => {
              try {
                await deleteJobPosting(record._id);
                await fetchJobPostings();
              } catch (error) {
                console.error("Error while deleting job posting or fetching data:", error);
              }
            }}
            onCancel={() => console.log("Cancelled")}
            okText="Yes"
            cancelText="No"
          >
            <Link className="confirm-text p-2" title="Delete job posting">
              <i data-feather="trash-2" className="feather-trash-2"></i>
            </Link>
          </Popconfirm>
        </td>
      ),
    },
  ];

  return (
    <div className="page-wrapper-lt">
      <div className="content">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="header-leave-container">
              <div>
                <ArrowLeft size={32} />
              </div>
              <div className="page-title">
                <h4>Job Postings</h4>
                <h6>Manage your job postings</h6>
              </div>
            </div>
            <div className="page-btn">
              <a
                className="btn btn-added"
                onClick={() => {
                  setOpenCreateModal(true);
                }}
                title="Create new job posting"
              >
                <PlusCircle className="me-2" />
                Job
              </a>
            </div>
          </div>
        </div>
        <div className="card table-list-card-grid">
          <div className="card-body" style={{ maxWidth: "100%", marginRight: "20px" }}>
            <div
              className="container mt-3"
              style={{
                maxWidth: "100%",
                marginLeft: "20px",
                marginRight: "40px",
              }}
            >
              <div className="row d-flex flex-wrap align-items-start justify-content-between">
                <div className="col-12 col-md-3 text-start">
                  <label className="form-label">Job Title</label>
                  <input
                    type="text"
                    value={title}
                    className="form-control"
                    onChange={(e) => setTitle(e.target.value)}
                    style={{ width: "80%", height: "40px" }}
                  />
                </div>
                <div className="col-12 col-md-3 mb-3 text-start">
                  <label className="form-label">Department</label>
                  <Select
                    className="select"
                    options={(allDepartments || []).map((dept) => ({
                      value: dept._id,
                      label: dept.departmentName,
                    }))}
                    placeholder="Select department"
                    value={department}
                    onChange={handleChangeDepartment}
                    allowClear
                    showSearch
                    filterOption={(input, option) =>
                      option.label.toLowerCase().includes(input.toLowerCase())
                    }
                    style={{ width: "80%", height: "40px" }}
                  />
                </div>
                <div className="col-12 col-md-3 mb-3 text-start">
                  <label className="form-label">Posted Date</label>
                  <DatePicker
                    className="form-control"
                    placeholder="Select posted date"
                    value={postedDate}
                    onChange={(date) => setPostedDate(date)}
                    style={{ width: "80%", height: "40px" }}
                    format="YYYY-MM-DD"
                  />
                </div>
              </div>
            </div>
            <div
              className="container mt-0"
              style={{
                maxWidth: "100%",
                marginLeft: "20px",
                marginRight: "40px",
              }}
            >
              <div className="row d-flex align-items-center justify-content-start">
                <div className="col-12 col-md-2 mb-3 text-start">
                  <button
                    disabled={loading}
                    className="btn btn-submit"
                    onClick={getSearchJobPostings}
                    style={{ height: "38px", width: "55%" }}
                  >
                    {loading ? "Searching..." : "Search"}
                  </button>
                  {loading && (
                    <div className="spinner">
                      <div className="loading-circle"></div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="card table-list-card-grid">
          <div className="card-body">
            <div className="table-top"></div>
            <div className="table-responsive">
              <Table
                loading={loading}
                columns={columns}
                dataSource={jobPostings}
                rowKey="_id"
                pagination={{
                  current: pagination.current,
                  pageSize: pagination.pageSize,
                  pageSizeOptions: ["5", "10", "20", "50"],
                  total: pagination.total,
                  showSizeChanger: true,
                  showQuickJumper: true,
                  showTotal: (total) => `Total ${total} items`,
                }}
                onChange={handleTableChange}
              />
            </div>
          </div>
        </div>
      </div>
      <Modal
        zIndex={200000}
        title={selectedJobPosting ? "Edit Job Posting" : "Create Job Posting"}
        open={openCreateModal}
        onCancel={() => {
          setSelectedJobPosting(null);
          setOpenCreateModal(false);
          form.resetFields();
        }}
        footer={null}
        maskClosable={false}
        closable={true}
      >
        <Form form={form} onFinish={onFinish} layout="vertical">
          <Form.Item
            name="title"
            label="Job Title"
            rules={[{ required: true, message: "Please enter the job title" }]}
          >
            <Input placeholder="Enter job title" />
          </Form.Item>
          <Form.Item
            name="department"
            label="Department"
            rules={[{ required: true, message: "Please select a department" }]}
          >
            <Select
              placeholder="Select department"
              options={allDepartments.map((dept) => ({
                value: dept._id,
                label: dept.departmentName,
              }))}
              showSearch
              filterOption={(input, option) =>
                option.label.toLowerCase().includes(input.toLowerCase())
              }
            />
          </Form.Item>
          <Form.Item
            name="description"
            label="Description"
            rules={[{ required: true, message: "Please enter the job description" }]}
          >
            <Input.TextArea placeholder="Enter job description" rows={4} />
          </Form.Item>
          <Form.Item
            name="requirements"
            label="Requirements"
            rules={[{ required: true, message: "Please enter the job requirements" }]}
          >
            <Input.TextArea
              placeholder="Enter requirements (comma-separated)"
              rows={4}
            />
          </Form.Item>
          <Form.List name="qualifications">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...restField }) => (
                  <div key={key} style={{ display: "flex", marginBottom: 8, alignItems: "center" }}>
                    <Form.Item
                      {...restField}
                      name={[name, "name"]}
                      rules={[{ required: true, message: "Please enter qualification name" }]}
                      style={{ flex: 1, marginRight: 8 }}
                    >
                      <Input placeholder="Qualification Name (e.g., Experience, MERN)" />
                    </Form.Item>
                    <Form.Item
                      {...restField}
                      name={[name, "expectedLevel"]}
                      rules={[{ required: true, message: "Please enter expected level" }]}
                      style={{ flex: 1, marginRight: 8 }}
                    >
                      <Input placeholder="Expected Level (e.g., 2 years, 10)" />
                    </Form.Item>
                    <Button type="link" onClick={() => remove(name)}>
                      Remove
                    </Button>
                  </div>
                ))}
                <Button type="dashed" onClick={() => add()} block>
                  Add Qualification
                </Button>
              </>
            )}
          </Form.List>
          <Form.Item
            name="dueDate"
            label="Due Date"
            rules={[{ required: true, message: "Please select the due date" }]}
          >
            <DatePicker
              className="form-control"
              placeholder="Select due date"
              style={{ width: "100%" }}
              format="YYYY-MM-DD"
            />
          </Form.Item>
          <Form.Item
            name="status"
            label="Status"
            rules={[{ required: true, message: "Please select a status" }]}
          >
            <Select
              placeholder="Select status"
              options={[
                { value: "Open", label: "Open" },
                { value: "Closed", label: "Closed" },
                { value: "Draft", label: "Draft" },
              ]}
            />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" loading={loading}>
              {selectedJobPosting ? "Update" : "Create"}
            </Button>
            <Button
              onClick={() => {
                setSelectedJobPosting(null);
                setOpenCreateModal(false);
                form.resetFields();
              }}
              style={{ marginLeft: 8 }}
            >
              Cancel
            </Button>
          </Form.Item>
        </Form>
      </Modal>
      <Modal
        title="Change Page Size"
        open={pageSizeModalVisible}
        onOk={handlePageSizeChange}
        onCancel={() => setPageSizeModalVisible(false)}
        footer={[
          <Button
            key="cancel"
            className="btn btn-cancel"
            onClick={() => setPageSizeModalVisible(false)}
          >
            Cancel
          </Button>,
          <Button key="submit" className="btn btn-submit" onClick={handlePageSizeChange}>
            Apply
          </Button>,
        ]}
      >
        <div className="form-group">
          <label>Items per page:</label>
          <Select
            style={{ width: "100%" }}
            value={tempPageSize}
            onChange={(value) => setTempPageSize(value)}
          >
            {pageSizeOptions.map((size) => (
              <Select.Option key={size} value={size}>
                {size}
              </Select.Option>
            ))}
          </Select>
        </div>
      </Modal>
    </div>
  );
};

export default JobPostings;